(() => {
  'use strict';
  const initializedCatalogs = new WeakSet();
  const acknowledgedForms = new WeakSet();
  const openers = new WeakMap();
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
  function openDialog(id, trigger) {
    const dialog = document.getElementById(id);
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    if (!dialog.open) { openers.set(dialog, trigger || document.activeElement); dialog.showModal(); }
    return true;
  }
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-pm-open]');
    if (trigger && openDialog(trigger.dataset.pmOpen, trigger)) event.preventDefault();
    const closer = event.target.closest('[data-pm-close]');
    if (closer) closer.closest('dialog')?.close();
  });
  document.querySelectorAll('.pm-dialog').forEach(dialog => {
    dialog.addEventListener('close', () => { const el = openers.get(dialog); if (el?.isConnected) el.focus(); });
  });
  function initCatalog(root) {
    if (initializedCatalogs.has(root) || root.dataset.pmComplete !== 'true') return;
    const grid = root.querySelector('[data-pm-grid]');
    const input = root.querySelector('[data-pm-search-input]');
    const type = root.querySelector('[data-pm-filter]');
    const sort = root.querySelector('[data-pm-sort]');
    const count = root.querySelector('[data-pm-results]');
    if (!grid || !input || !type || !sort || !count) return;
    initializedCatalogs.add(root);
    const cards = [...grid.querySelectorAll('[data-pm-card]')];
    const index = new Map(cards.map((card, n) => [card, n]));
    root.querySelector('[data-pm-filters]').hidden = false;
    function update() {
      const terms = normalize(input.value).split(' ').filter(Boolean);
      const ordered = [...cards].sort((a, b) => {
        if (sort.value === 'az') return a.dataset.pmName.localeCompare(b.dataset.pmName, document.documentElement.lang);
        if (sort.value === 'low') return Number(a.dataset.pmPrice) - Number(b.dataset.pmPrice);
        if (sort.value === 'high') return Number(b.dataset.pmPrice) - Number(a.dataset.pmPrice);
        return index.get(a) - index.get(b);
      });
      let visible = 0;
      const fragment = document.createDocumentFragment();
      ordered.forEach(card => {
        card.hidden = !terms.every(term => normalize(card.dataset.pmSearch).includes(term)) || Boolean(type.value && card.dataset.pmType !== type.value);
        if (!card.hidden) visible++;
        fragment.appendChild(card);
      });
      grid.appendChild(fragment);
      count.textContent = root.dataset.pmCountPattern.replace('__COUNT__', String(visible));
      root.querySelector('[data-pm-empty]').hidden = visible !== 0;
    }
    let timer;
    input.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(update, 100); });
    type.addEventListener('change', update); sort.addEventListener('change', update);
    root.querySelector('[data-pm-search-form]').addEventListener('submit', e => { e.preventDefault(); clearTimeout(timer); update(); });
    root.querySelector('[data-pm-reset]').addEventListener('click', () => { input.value = ''; type.value = ''; sort.value = 'default'; update(); input.focus(); });
    update();
  }
  function panelFor(formId) { return [...document.querySelectorAll('[data-pm-consent]')].find(el => el.dataset.pmForm === formId); }
  function setup() {
    document.querySelectorAll('[data-pm-catalog]').forEach(initCatalog);
    document.querySelectorAll('[data-pm-checkout]').forEach(button => {
      const panel = panelFor(button.getAttribute('form') || button.form?.id);
      button.disabled = button.dataset.pmBusy === 'true' || !panel || panel.dataset.pmReady !== 'true';
    });
  }
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const button = event.submitter || form.querySelector('[data-pm-checkout]') || document.querySelector(`[data-pm-checkout][form="${CSS.escape(form.id)}"]`);
    if (!button?.matches('[data-pm-checkout]')) return;
    if (acknowledgedForms.has(form)) { acknowledgedForms.delete(form); return; }
    event.preventDefault(); event.stopImmediatePropagation();
    const panel = panelFor(form.id);
    if (!panel || panel.dataset.pmReady !== 'true' || button.dataset.pmBusy === 'true') return;
    const error = panel.querySelector('[data-pm-error]');
    const fields = [...panel.querySelectorAll('input, textarea')];
    const invalid = fields.find(field => !field.checkValidity() || (field.type !== 'checkbox' && !field.value.trim()));
    if (invalid) { error.textContent = panel.dataset.invalid; invalid.focus(); invalid.reportValidity(); return; }
    const attributes = {
      'Research organization': panel.querySelector('input[type=text]').value.trim(),
      'Research purpose': panel.querySelector('textarea').value.trim(),
      'Research-only acknowledgment': 'Accepted: age 21+, qualified laboratory research only; no human or animal administration.',
      'Terms version': panel.dataset.pmVersion,
      'Acknowledged at (client UTC)': new Date().toISOString()
    };
    button.disabled = true; button.dataset.pmBusy = 'true'; error.textContent = panel.dataset.saving;
    const controller = new AbortController(); const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(panel.dataset.pmUpdate, {method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({attributes}),signal:controller.signal});
      if (!response.ok) throw new Error('Cart update failed');
      const saved = await response.json();
      if (!Object.keys(attributes).every(key => saved.attributes?.[key] === attributes[key])) throw new Error('Confirmation not persisted');
      // Let the original submit dispatch finish before resubmitting, including cached responses.
      await new Promise(resolve => setTimeout(resolve, 0));
      if (!form.isConnected || !panel.isConnected || !button.isConnected || panel.dataset.pmReady !== 'true') throw new Error('Cart changed during confirmation');
      if (panel.querySelector('input[type=text]').value.trim() !== attributes['Research organization'] || panel.querySelector('textarea').value.trim() !== attributes['Research purpose'] || !panel.querySelector('input[type=checkbox]').checked) throw new Error('Acknowledgment changed during confirmation');
      error.textContent = ''; delete button.dataset.pmBusy; button.disabled = false;
      acknowledgedForms.add(form);
      try { form.requestSubmit(button); } finally { acknowledgedForms.delete(form); }
    } catch (_) { error.textContent = panel.dataset.error; }
    finally { clearTimeout(timeout); delete button.dataset.pmBusy; setup(); }
  }, true);
  let queued = false;
  new MutationObserver(records => {
    if (queued || !records.some(record => [...record.addedNodes].some(n => n.nodeType === 1 && (n.matches?.('[data-pm-consent],[data-pm-checkout],[data-pm-catalog]') || n.querySelector?.('[data-pm-consent],[data-pm-checkout],[data-pm-catalog]'))))) return;
    queued = true; requestAnimationFrame(() => { queued = false; setup(); });
  }).observe(document.body, {childList:true,subtree:true});
  document.addEventListener('shopify:section:load', setup); setup();
  const header = document.querySelector('[data-pm-header]');
  if (header) {
    const resize = () => document.documentElement.style.setProperty('--pm-header-height', `${header.getBoundingClientRect().height}px`);
    if ('ResizeObserver' in window) new ResizeObserver(resize).observe(header); else window.addEventListener('resize', resize);
    let tick = false;
    window.addEventListener('scroll', () => { if (!tick) { tick = true; requestAnimationFrame(() => { header.classList.toggle('is-scrolled', window.scrollY > 48); tick = false; }); } }, {passive:true});
    header.classList.toggle('is-scrolled', window.scrollY > 48); resize();
  }
  const gate = document.getElementById('pm-age-gate');
  if (gate) {
    const key = 'pm-research-entry-v1'; let accepted = false;
    try { accepted = sessionStorage.getItem(key) === 'accepted'; } catch (_) {}
    if (!accepted) openDialog('pm-age-gate');
    gate.addEventListener('cancel', e => e.preventDefault());
    gate.querySelector('form').addEventListener('submit', event => {
      event.preventDefault(); if (!event.target.reportValidity()) return;
      try { sessionStorage.setItem(key, 'accepted'); } catch (_) {} gate.close();
    });
  }
})();
